import angular from 'angular';
import csmTools from '../../util/tools/tools';

export default angular
  .module('csm.ui.inlineLabelGroup', [csmTools])

  /*******************************************************************************
   * Directive: inlineLabelGroup */
  /**
   * This is used to ensure that all inlineInputs have the same label-width for
   * proper alignment. When a name is specified with the attribute, multiple groups
   * can be looked at that share the name to manage the width across all the groups.
   *******************************************************************************/
  .directive('inlineLabelGroup', function (Tools) {
    /** Global Group Hash Object. */ var mGroups = {};

    /*****************************************************************************
     * addToGroup */
    /**
     * Adds the controller instance for the inlineLabelGroup to the groups hash
     * for the provided key.
     *
     * @param key   Key name to update
     * @param ctrl  Controller instance to add
     *****************************************************************************/
    var addToGroup = function (key, ctrl) {
      if (!mGroups.hasOwnProperty(key)) mGroups[key] = [];

      if (mGroups[key].indexOf(ctrl) > -1) return;

      mGroups[key].push(ctrl);
      refreshGroup(key);
    };

    /*****************************************************************************
     * refreshGroup */
    /**
     * Refresh the group for the provided key to set the largest width.
     *
     * @param key        Group key name to refresh
     * @param noRefresh  Optional controller to ignore for refresh call
     *****************************************************************************/
    var refreshGroup = function (key, noRefresh) {
      var group = mGroups[key] || Array.prototype.slice.call(arguments, 1);

      /*----------------------------------------*/
      /* Find the largest width from all groups */
      /*----------------------------------------*/
      var maxWidth;
      group.forEach(function (ctrl) {
        /*------------------------------------*/
        /* Update width calculation if needed */
        /*------------------------------------*/
        if (ctrl != noRefresh) ctrl.refreshWidth(true);

        var width = ctrl.getWidth();

        if (!maxWidth || width > maxWidth) maxWidth = width;
      });

      group.forEach(function (ctrl) {
        ctrl.setWidth(maxWidth);
      });
    };

    /*****************************************************************************
     * removeFromGroup */
    /**
     * Removes the controller from the group for the provided key.
     *
     * @param key   Key name to update
     * @param ctrl  Controller instance to remove
     *****************************************************************************/
    var removeFromGroup = function (key, ctrl) {
      if (!mGroups.hasOwnProperty(key) || mGroups[key].indexOf(ctrl) == -1)
        return;

      mGroups[key].splice(mGroups[key].indexOf(ctrl), 1);

      /*-------------------------------------------*/
      /* Clean up the hash when the group is empty */
      /*-------------------------------------------*/
      if (mGroups[key].length == 0) delete mGroups[key];
      else refreshGroup(key);
    };

    return {
      restrict: 'A',
      controller: function ($scope, $interval, $element) {
        /** Controller Reference. */ var ctrl = this;
        /** List of child objects to check. */ var mChildren = [];
        /** Largest-width child. */ var mMaxChild;

        /** Group Name. */
        var groupName = function () {
          return $element.attr('inline-label-group');
        };

        /** Checks if element has been drawn. */
        var isDisplayed = function () {
          return $element[0].offsetWidth != 0;
        };

        /*************************************************************************
         * add */
        /**
         * Adds a child object to check widths against.
         *
         * @param child  Child object to add
         *************************************************************************/
        ctrl.add = function (child) {
          mChildren.push(child);
          this.refreshWidth();
        };

        /*************************************************************************
         * getWidth */
        /**
         * Returns the max width for the group from the max child if set.
         *
         * @return Width of the mMaxChild. 0 if null
         *************************************************************************/
        ctrl.getWidth = function () {
          return mMaxChild ? mMaxChild.getWidth() : 0;
        };

        /*************************************************************************
         * refreshWidth */
        /**
         * Searches through the children to find the largest width child. No update
         * of child widths is done if preventUpdate is true.
         *
         * @param preventUpdate  Prevents update of child widths
         *************************************************************************/
        ctrl.refreshWidth = function (preventUpdate) {
          mMaxChild = null;

          /*--------------------------------------------------*/
          /* Do nothing if there are no children or if this   */
          /* component has not been added to the document yet */
          /*--------------------------------------------------*/
          if (mChildren.length == 0 || !isDisplayed()) return;

          /*---------------------------------------------------------------*/
          /* Clear the set width to find full width needed for child label */
          /*---------------------------------------------------------------*/
          mChildren.forEach(function (c) {
            c.setWidth(0);

            if (!mMaxChild || c.getWidth() > mMaxChild.getWidth())
              mMaxChild = c;
          });

          /*----------------------------------------*/
          /* Return if no update is to be performed */
          /*----------------------------------------*/
          if (preventUpdate) return;
          else refreshGroup(groupName(), ctrl);
        };

        /*************************************************************************
         * remove */
        /**
         * Removes a child object from being monitored. Refreshes widths if child
         * was the max child.
         *
         * @param child  Child object to remove
         *************************************************************************/
        ctrl.remove = function (child) {
          var idx = mChildren.indexOf(child);
          if (idx == -1) return;

          mChildren.splice(idx, 1);

          if (mMaxChild == child) this.refreshWidth();
        };

        /*************************************************************************
         * setWidth */
        /**
         * Sets the width on the child objects
         *
         * @param width Width to set
         *************************************************************************/
        ctrl.setWidth = function (width) {
          mChildren.forEach(function (c) {
            c.setWidth(width);
          });
        };

        /*------------------------------------------*/
        /* Watch for group name changes and respond */
        /*------------------------------------------*/
        $scope.$watch(groupName, function (newName, oldName) {
          var hasOldName = !Tools.isEmpty(oldName);
          var hasNewName = !Tools.isEmpty(newName);

          if (hasOldName) removeFromGroup(oldName, ctrl);

          if (hasNewName) addToGroup(newName, ctrl);
        });

        /*--------------------------------------*/
        /* Remove group when scope is destroyed */
        /*--------------------------------------*/
        $scope.$on('$destroy', function () {
          if (!Tools.isEmpty(groupName())) removeFromGroup(groupName(), ctrl);
        });

        /*----------------------------------------------------*/
        /* If this object is not currently display, check for */
        /* when the object is displayed to refresh the widths */
        /*----------------------------------------------------*/
        if (!isDisplayed())
          var displayWatch = $interval(function () {
            if (!isDisplayed()) return;

            ctrl.refreshWidth();
            $interval.cancel(displayWatch);
          }, 200);
      }
    };
  }).name;
